/**
 * Shared report column definitions.
 * - key:       backend field name (used in hidden_columns, column filtering)
 * - label:     human-readable label (used in Settings toggles, confirm modals)
 * - formField: local form field name in CostProductionBreakdownForm
 */
export const REPORT_COLUMN_CONFIGS = [
  { key: "lab_costs", label: "Lab Costs", formField: "lab" },
  { key: "material_costs", label: "Material Costs", formField: "material" },
  { key: "invisalign_costs", label: "Invisalign Costs", formField: "invisalign" },
  { key: "anesthesia_fee", label: "Anesthesia Fee", formField: "anesthesia_fee" },
  { key: "anesthesiologist_fee", label: "Anesthesiologist Fee", formField: "anesthesiologist_fee" },
  { key: "facility_fee", label: "Facility Fee", formField: "facility_fee" },
  { key: "botox", label: "Botox", formField: "botox" },
  {
    key: "production_adjustment",
    label: "Production Adjustment",
    formField: "production_adjustment",
  },
  {
    key: "collection_adjustment",
    label: "Collection Adjustment",
    formField: "collection_adjustment",
  },
];

// Lead categories for patient intake forms
export const LEAD_CATEGORIES = [
  "Email Blast/Marketing",
  "Local Sponsorship",
  "Friends/Family Referral",
  "Lives/Works in the Area",
  "Direct Mail Campaigns",
  "Walk In/Emergency Patient",
  "Curbex/Building External Sign",
  "Promotions/Special Offers",
  "Former Patient",
  "Emergency Patients",
  "Online Directories",
  "Community Events",
  "Staff/Internal Referral",
  "Social Media",
  "Doctor/GDP Referral",
  "Google/Internet",
  "Other",
];

// Audit log action options for filter dropdown
export const ACTION_OPTIONS = [
  { value: "", label: "All Actions" },
  { value: "CREATE", label: "Create" },
  { value: "UPDATE", label: "Update" },
  { value: "DELETE", label: "Delete" },
];

// Audit log action badge colors
export const ACTION_COLORS = {
  CREATE: "success",
  UPDATE: "warning",
  DELETE: "danger",
  OTHER: "default",
};

// Maps API endpoint segments to friendly names.
// Keys are the first path segment after /api/ (see config/urls.py) — the audit log
// middleware records every non-GET /api/ request, so every route mounted there
// needs an entry or the "Where" column falls back to a capitalized slug.
export const ENDPOINT_LABELS = {
  // Reporting
  reports: "Reports",
  overview: "Overview",
  forecasts: "Forecasts",
  "collection-breakdown": "Collection Breakdown",
  "monthly-compensation": "Monthly Compensation",
  "lab-invoices": "Lab Invoices",
  // Clinics
  clinics: "Clinics",
  "clinic-vacations": "Clinic Vacations",
  "clinic-schedule-overrides": "Clinic Schedule Overrides",
  // Practitioners & patients
  practitioners: "Practitioners",
  "practitioner-profiles": "Practitioner Profiles",
  "practitioner-vacations": "Practitioner Vacations",
  "practitioner-weekly-schedules": "Practitioner Weekly Schedules",
  "practitioner-schedule-overrides": "Practitioner Schedule Overrides",
  patients: "Patients",
  // Cash & accounting
  "cash-transaction": "Cash Transaction",
  "daily-bank-balance": "Daily Bank Balance",
  transfers: "Transfers",
  "schedule-payment": "Schedule Payment",
  netsuite: "NetSuite",
  // Admin
  users: "Users",
  roles: "Roles",
  auth: "Authentication",
  "audit-logs": "Audit Logs",
};

// Sub-paths worth filtering on their own even though the "Where" column shows
// them under their parent segment. The backend matches `endpoint` by icontains,
// so a nested path works as a filter value.
const ENDPOINT_SUBPATH_LABELS = {
  "cash-management-journal-entries": "Cash Management",
};

// Where filter dropdown options — derived so it can never drift from the labels above
export const WHERE_OPTIONS = [
  { value: "", label: "All" },
  ...Object.entries({ ...ENDPOINT_LABELS, ...ENDPOINT_SUBPATH_LABELS }).map(([value, label]) => ({
    value,
    label,
  })),
];

// ADP CO code → NetSuite clearing/cash account details.
// acctNumber: resolves placeholder values in ADP payroll CSVs (e.g. "10111XX").
// acctId / subsidiaryId / subsidiaryName: hardcoded so no API lookup is needed at runtime.
export const ADP_CO_ACCOUNT_MAP = {
  "09O": {
    acctNumber: "1011350",
    acctId: 6300,
    subsidiaryId: 78,
    subsidiaryName: "Bells Corners Dental",
  },
  "0EN": {
    acctNumber: "1011297",
    acctId: 6033,
    subsidiaryId: 79,
    subsidiaryName: "Landmark Dental Care",
  },
  CED: {
    acctNumber: "1011187",
    acctId: 5193,
    subsidiaryId: 59,
    subsidiaryName: "Oral Surgery - Kevin J. McCann",
  },
  L6U: {
    acctNumber: "1011193",
    acctId: 5402,
    subsidiaryId: 61,
    subsidiaryName: "Deer Ridge Dental",
  },
  P4D: {
    acctNumber: "1011195",
    acctId: 5413,
    subsidiaryId: 63,
    subsidiaryName: "Waterford Dental",
  },
  SCO: {
    acctNumber: "1011186",
    acctId: 5170,
    subsidiaryId: 56,
    subsidiaryName: "Clinique Dentaire de la Peninsule",
  },
  SL9: {
    acctNumber: "10113116",
    acctId: 6170,
    subsidiaryId: 58,
    subsidiaryName: "Ellesmere Dental",
  },
  SUB: {
    acctNumber: "1011283",
    acctId: 5510,
    subsidiaryId: 72,
    subsidiaryName: "York Street Cornwall",
  },
  TQ2: { acctNumber: "1011301-", acctId: 6140, subsidiaryId: 76, subsidiaryName: "Dr. Russ" },
  UL6: {
    acctNumber: "1011197",
    acctId: 5426,
    subsidiaryId: 67,
    subsidiaryName: "Dr. Frances Ross",
  },
  WS6: { acctNumber: "1011285", acctId: 5512, subsidiaryId: 74, subsidiaryName: "Dr. Joice Lui" },
  Y2Q: {
    acctNumber: "1011196",
    acctId: 5417,
    subsidiaryId: 64,
    subsidiaryName: "Tillsonburg Dental",
  },
  YVX: {
    acctNumber: "1011192",
    acctId: 5395,
    subsidiaryId: 60,
    subsidiaryName: "Arden Park Dental",
  },
};

// NetSuite internal account IDs used when building journal entry lines.
export const NETSUITE_ACCOUNT_IDS = {
  // 1013001 Accounts Receivable- Control : Accounts Receivable
  ACCOUNTS_RECEIVABLE: 1044,
  // 2012402 Accrued Liabilities : Employee Payable : Associates Fee Payable
  ASSOCIATES_FEE_PAYABLE: 2307,
  // Accrued Liabilities : Employee Payable : Health Benefit Payable
  HEALTH_BENEFIT_PAYABLE: 2591,
  // Operating Expenses : Corporate Expenses : Corp Exp: Corporate Contribution :
  // Payroll: Health Benefits-Practices
  HEALTH_BENEFITS_PRACTICES: 5499,
  // 1014002 Other Current Asset : Collection Account - Practices
  COLLECTION_ACCOUNT_PRACTICES: 2608,
  // Bank Balance : RBC S.F. Corporation - [9411]
  RBC_SF_CORPORATION_9411: 1009,
  // Subsidiary : Smiles First Corporation (parent)
  SMILES_FIRST_CORPORATION: 1,
  // Long Term Loan : Long Term Loan :: Financial Institution [Others] :
  // 2020240 Long Term Loan : Long Term Loan :: Financial Institution [Others] : Loan:  From Smiles First Corporation
  LOAN_FROM_SMILES_FIRST_CORPORATION: 4940,
  // Cash in Hand : Cash SFC
  CASH_SFC: 1027,
};

// ADP 2026 pay-period calendar.
// - month:         payroll period label with sequence, e.g. "January 2026 (1)"
// - start / end:   pay period covered (MM/DD/YYYY)
// - directDeposit: direct-deposit date (MM/DD/YYYY)
// Note: source listed period 21's direct deposit as 10/09/2025 — corrected to 2026.
export const ADP_PAY_PERIODS = [
  {
    month: "January 2026 (1)",
    start: "12/14/2025",
    end: "12/27/2025",
    directDeposit: "01/02/2026",
  },
  {
    month: "January 2026 (2)",
    start: "12/28/2025",
    end: "01/10/2026",
    directDeposit: "01/16/2026",
  },
  {
    month: "January 2026 (3)",
    start: "01/11/2026",
    end: "01/24/2026",
    directDeposit: "01/30/2026",
  },
  {
    month: "February 2026 (1)",
    start: "01/25/2026",
    end: "02/07/2026",
    directDeposit: "02/13/2026",
  },
  {
    month: "February 2026 (2)",
    start: "02/08/2026",
    end: "02/21/2026",
    directDeposit: "02/27/2026",
  },
  { month: "March 2026 (1)", start: "02/22/2026", end: "03/07/2026", directDeposit: "03/13/2026" },
  { month: "March 2026 (2)", start: "03/08/2026", end: "03/21/2026", directDeposit: "03/27/2026" },
  { month: "April 2026 (1)", start: "03/22/2026", end: "04/04/2026", directDeposit: "04/10/2026" },
  { month: "April 2026 (2)", start: "04/05/2026", end: "04/18/2026", directDeposit: "04/24/2026" },
  { month: "May 2026 (1)", start: "04/19/2026", end: "05/02/2026", directDeposit: "05/08/2026" },
  { month: "May 2026 (2)", start: "05/03/2026", end: "05/16/2026", directDeposit: "05/22/2026" },
  { month: "June 2026 (1)", start: "05/17/2026", end: "05/30/2026", directDeposit: "06/05/2026" },
  { month: "June 2026 (2)", start: "05/31/2026", end: "06/06/2026", directDeposit: "06/12/2026" },
  { month: "June 2026 (3)", start: "06/07/2026", end: "06/20/2026", directDeposit: "06/26/2026" },
  { month: "July 2026 (1)", start: "06/21/2026", end: "07/04/2026", directDeposit: "07/10/2026" },
  { month: "July 2026 (2)", start: "07/05/2026", end: "07/18/2026", directDeposit: "07/24/2026" },
  { month: "August 2026 (1)", start: "07/19/2026", end: "08/01/2026", directDeposit: "08/07/2026" },
  { month: "August 2026 (2)", start: "08/02/2026", end: "08/15/2026", directDeposit: "08/21/2026" },
  {
    month: "September 2026 (1)",
    start: "08/16/2026",
    end: "08/29/2026",
    directDeposit: "09/04/2026",
  },
  {
    month: "September 2026 (2)",
    start: "08/30/2026",
    end: "09/12/2026",
    directDeposit: "09/18/2026",
  },
  {
    month: "October 2026 (1)",
    start: "09/13/2026",
    end: "09/26/2026",
    directDeposit: "10/02/2026",
  },
  {
    month: "October 2026 (2)",
    start: "09/27/2026",
    end: "10/10/2026",
    directDeposit: "10/16/2026",
  },
  {
    month: "October 2026 (3)",
    start: "10/11/2026",
    end: "10/24/2026",
    directDeposit: "10/30/2026",
  },
  {
    month: "November 2026 (1)",
    start: "10/25/2026",
    end: "11/07/2026",
    directDeposit: "11/13/2026",
  },
  {
    month: "November 2026 (2)",
    start: "11/08/2026",
    end: "11/21/2026",
    directDeposit: "11/27/2026",
  },
  {
    month: "December 2026 (1)",
    start: "11/22/2026",
    end: "12/05/2026",
    directDeposit: "12/11/2026",
  },
  {
    month: "December 2026 (2)",
    start: "12/06/2026",
    end: "12/19/2026",
    directDeposit: "12/25/2026",
  },
  {
    month: "January 2027 (1)",
    start: "12/20/2026",
    end: "01/02/2027",
    directDeposit: "01/08/2027",
  },
];
